import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import styled, { css } from 'styled-components';
import { useEventHandler } from '@famly/stat_ts-utils_observable-hooks';
import { hasValue } from '@famly/stat_ts-utils_has-value';
import { useSyncRef } from '@famly/stat_ts-utils_use-sync-ref';
import ProgressPills from 'web-app/react/components/progress-pills/progress-pills';
import { addHover, media } from 'web-app/styleguide/utils';
import Backspace from 'web-app/react/components/icons/legacy/lazy-icons/backspace';
import PlatformHelper from 'web-app/helpers/platform-helper';
import { getSpacing, s8 } from 'web-app/styleguide/spacing';
import { Base } from 'web-app/react/components/layout/layout';
const KeyCode = {
    Zero: '0',
    One: '1',
    Two: '2',
    Three: '3',
    Four: '4',
    Five: '5',
    Six: '6',
    Seven: '7',
    Eight: '8',
    Nine: '9',
    Backspace: 'Backspace',
    __CONTEXTUAL: '__CONTEXTUAL',
    __EMPTY: '__EMPTY',
};
const KeyCodeValues = [
    KeyCode.Zero,
    KeyCode.One,
    KeyCode.Two,
    KeyCode.Three,
    KeyCode.Four,
    KeyCode.Five,
    KeyCode.Six,
    KeyCode.Seven,
    KeyCode.Eight,
    KeyCode.Nine,
    KeyCode.Backspace,
    KeyCode.__CONTEXTUAL,
    KeyCode.__EMPTY,
];
const makeValue = (prev, action) => {
    switch (action) {
        case KeyCode.Backspace:
            if (!hasValue(prev) || prev.length === 0) {
                return prev;
            }
            return prev.slice(0, -1);
        case KeyCode.__CONTEXTUAL:
        case KeyCode.__EMPTY:
            return prev;
        default:
            return `${hasValue(prev) ? prev : ''}${action}`;
    }
};
export const PIN = ({ onChange, pinSize, value, onFinish, contextualButton, className, ...rest }) => {
    const valueRef = React.useRef(value);
    useSyncRef(valueRef, value);
    const handleChange = React.useCallback((newValue) => {
        onChange(newValue);
        if (hasValue(newValue) && newValue.length === pinSize) {
            onFinish(newValue);
        }
    }, [onChange, onFinish, pinSize]);
    useEventHandler({
        target: React.useRef(document),
        eventName: 'keydown',
        debounce: 0,
        handler: React.useCallback((e) => {
            if (e.metaKey || e.shiftKey || e.ctrlKey || e.altKey) {
                return;
            }
            const keyCode = e.key;
            if (!KeyCodeValues.includes(keyCode)) {
                return;
            }
            handleChange(makeValue(valueRef.current, keyCode));
        }, [valueRef, handleChange]),
    });
    const handleClick = React.useCallback((e) => {
        if (!('getAttribute' in e.target)) {
            return;
        }
        const target = e.target;
        const targetValue = target.getAttribute('data-pin-value');
        if (!hasValue(targetValue) || !Object.values(KeyCode).includes(targetValue)) {
            return;
        }
        if (targetValue === KeyCode.__CONTEXTUAL && hasValue(contextualButton)) {
            contextualButton.onClick();
        }
        else {
            handleChange(makeValue(valueRef.current, targetValue));
        }
    }, [contextualButton, handleChange, valueRef]);
    const isTouch = PlatformHelper.isTouch();
    const handlers = {
        onClick: isTouch ? undefined : handleClick,
        onTouchStart: isTouch ? handleClick : undefined,
    };
    return (_jsxs("div", { ...handlers, className: className, children: [rest.withPills ? (_jsx(Base, { padding: "0 10%", children: _jsx(ProgressPills, { appearance: 'Simple', count: pinSize, activeCount: value ? value.length : 0, pillState: getPillState(rest.pinState) }) })) : null, _jsx(Static, { contextualButton: contextualButton })] }));
};
export const getPillState = (pinState) => {
    switch (pinState) {
        case 'Loading':
            return 'Loading';
        case 'Error':
            return 'Error';
        case 'Success':
        default:
            return 'Success';
    }
};
const Static = React.memo(({ contextualButton }) => {
    return (_jsxs(PINButtonContainer, { children: [_jsx(PINButton, { display: "1", value: KeyCode.One }), _jsx(PINButton, { display: "2", value: KeyCode.Two }), _jsx(PINButton, { display: "3", value: KeyCode.Three }), _jsx(PINButton, { display: "4", value: KeyCode.Four }), _jsx(PINButton, { display: "5", value: KeyCode.Five }), _jsx(PINButton, { display: "6", value: KeyCode.Six }), _jsx(PINButton, { display: "7", value: KeyCode.Seven }), _jsx(PINButton, { display: "8", value: KeyCode.Eight }), _jsx(PINButton, { display: "9", value: KeyCode.Nine }), hasValue(contextualButton) ? (_jsx(PINButton, { display: contextualButton.icon, value: KeyCode.__CONTEXTUAL })) : (_jsx(PINButton, { display: "", value: KeyCode.__EMPTY })), _jsx(PINButton, { display: "0", value: KeyCode.Zero }), _jsx(PINButton, { display: _jsx(StyledBackspace, {}), value: KeyCode.Backspace })] }));
});
export const StyledBackspace = styled(Backspace).attrs(props => ({
    fill: props.theme.text,
    size: 48,
})) ``;
export const StyledPINButton = styled.button ``;
export const PINButtonContainer = styled.div `
    margin-top: ${getSpacing(s8)};
    display: grid;
    grid-template-columns: repeat(3, 33.333%);
    grid-template-rows: repeat(4, 80px);

    ${StyledPINButton} {
        border: none;
        font-size: 36px;
        line-height: 1;
        font-weight: 600;
        color: ${props => props.theme.text};
        background: ${props => props.theme.invertedText};
        cursor: pointer;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;

        & > * {
            pointer-events: none;
        }

        border-top: 1px solid ${props => props.theme.textDisabled};
        border-right: 1px solid ${props => props.theme.textDisabled};
        &:nth-child(-n + 3) {
            border-top: none;
        }
        &:nth-child(3n) {
            border-right: none;
        }
        ${addHover(css `
            &:hover {
                background: ${props => props.theme.backgroundHover};
            }
        `)}

        &:focus {
            outline: none;
        }
    }

    ${media.mobile `
        ${StyledPINButton} {
            font-size: 32px;
        }
        grid-template-rows: repeat(4, 10vh);
    `}
    ${media.mobileSideways `
        ${StyledPINButton} {
            font-size: ${props => props.theme.fontConfiguration.sizes.Title} !important;
        }
        grid-template-rows: repeat(4, 10vh);
    `}
`;
const PINButton = ({ display, value }) => {
    return (_jsx(StyledPINButton, { type: "button", "data-pin-value": value, children: display }));
};
